import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPlus,
  faEdit,
  faTrash,
  faSpinner,
  faTag,
  faDoorOpen,
  faIdCard,
  faCalendarAlt,
  faHotel
} from '@fortawesome/free-solid-svg-icons';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import { useTheme } from '../../context/ThemeContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import RoomManagementModal from './RoomManagementModal';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import moment from 'moment';
import styles from './RoomDetails.module.css';

const RoomDetails = () => {
  const { eventId, dateId, hotelId } = useParams();
  const navigate = useNavigate();

  const { userData } = useContext(UserContext);
  const { selectedEvent, updateDate, getSelectedDate, setSelectedEvent } = useContext(TourContext);
  const { isDark } = useTheme();
  
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingText, setProcessingText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [events, setEvents] = useState([]);
  
  // Determine event type from URL or context
  const eventType = selectedEvent?.type || (eventId && eventId.startsWith('tour-') ? 'tour' : 'show');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchHotelAndRooms = async () => {
      try {
        setIsLoading(true);
        
        if (!eventId || !dateId || !hotelId) {
          throw new Error('Missing required parameters');
        }
        
        const db = getFirestoreInstance();
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        
        // Get hotel data
        const hotelRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'hotels', hotelId);
        const hotelDoc = await getDoc(hotelRef);
        
        if (!hotelDoc.exists()) {
          throw new Error('Hotel not found');
        }
        
        const hotelData = {
          id: hotelDoc.id,
          ...hotelDoc.data()
        };
        
        setHotel(hotelData);
        
        // Get rooms data
        const roomsQuery = collection(db, collectionName, eventId, 'eventDates', dateId, 'hotels', hotelId, 'rooms');
        const roomsSnapshot = await getDocs(roomsQuery);
        
        const roomsData = roomsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setRooms(roomsData);
        
      } catch (error) {
        console.error('Error fetching hotel and rooms data:', error);
        navigate(`/${eventType}/${eventId}/${dateId}`); // Navigate back on error
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchHotelAndRooms();
  }, [eventId, dateId, hotelId, navigate, eventType]);

  const handleAddRoom = () => {
    setEditingRoom(null);
    setIsModalVisible(true);
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    setIsModalVisible(true);
  };

  const handleSaveRoom = async (roomData) => {
    try {
      setIsProcessing(true);
      setProcessingText(editingRoom ? 'Updating room...' : 'Adding room...');
      
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';

      if (editingRoom) {
        // Update existing room
        const roomRef = doc(
          db, 
          collectionName, 
          eventId, 
          'eventDates', 
          dateId, 
          'hotels', 
          hotelId, 
          'rooms', 
          editingRoom.id
        );
        
        await updateDoc(roomRef, {
          ...roomData,
          updatedAt: serverTimestamp()
        });
      } else {
        // Add new room
        await addDoc(
          collection(db, collectionName, eventId, 'eventDates', dateId, 'hotels', hotelId, 'rooms'),
          {
            ...roomData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          }
        );
      }
      
      // Update hotel document with room count
      const updatedRoomCount = editingRoom ? rooms.length : rooms.length + 1;
      const hotelRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'hotels', hotelId);
      
      await updateDoc(hotelRef, {
        rooms: updatedRoomCount,
        updatedAt: serverTimestamp()
      });
      
      // Update the event date to trigger listener
      const eventDateRef = doc(db, collectionName, eventId, 'eventDates', dateId);
      await updateDoc(eventDateRef, {
        dateLastUpdated: serverTimestamp()
      });
      
      // Refresh the data
      const updatedRoomsList = editingRoom
        ? rooms.map(room => (room.id === editingRoom.id ? { ...roomData, id: room.id } : room))
        : [...rooms, { ...roomData, id: `temp-${Date.now()}` }];
      
      setRooms(updatedRoomsList);
      
      // Let the listener update the state
      await updateDate(eventId, dateId, eventType);
      
    } catch (error) {
      console.error('Error saving room:', error);
      alert('Failed to save room. Please try again.');
    } finally {
      setIsProcessing(false);
      setProcessingText('');
      setIsModalVisible(false);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;
    
    try {
      setIsProcessing(true);
      setProcessingText('Deleting room...');
      
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Delete the room document
      const roomRef = doc(
        db, 
        collectionName, 
        eventId, 
        'eventDates', 
        dateId, 
        'hotels', 
        hotelId, 
        'rooms', 
        roomId
      );
      
      await deleteDoc(roomRef);
      
      // Update hotel document with new room count
      const hotelRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'hotels', hotelId);
      
      await updateDoc(hotelRef, {
        rooms: rooms.length - 1,
        updatedAt: serverTimestamp()
      });
      
      // Update the event date to trigger listener
      const eventDateRef = doc(db, collectionName, eventId, 'eventDates', dateId);
      await updateDoc(eventDateRef, {
        dateLastUpdated: serverTimestamp()
      });
      
      // Update local state
      setRooms(rooms.filter(room => room.id !== roomId));
      
      // Let the listener update the state
      await updateDate(eventId, dateId, eventType);
      
    } catch (error) {
      console.error('Error deleting room:', error);
      alert('Failed to delete room. Please try again.');
    } finally {
      setIsProcessing(false);
      setProcessingText('');
    }
  };

  const getTitle = () => {
    if (!hotel) return 'Room Details';
    
    if (hotel.group === 'Custom' && hotel.customGroupName) {
      return hotel.customGroupName !== 'All' 
        ? `${hotel.customGroupName}'s Hotel Rooms` 
        : 'Hotel Rooms';
    } else {
      return hotel.group !== 'All' 
        ? `${hotel.group}'s Hotel Rooms` 
        : 'Hotel Rooms';
    }
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
  };
  
  const handleBack = () => {
    navigate(`/${eventType}/${eventId}/${dateId}`);
  };

  if (isLoading) {
    return (
      <div className={styles.layout}>
        <div className={styles.mainLayout}>
          {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
          <main className={styles.content}>
            <div className={styles.loadingContainer}>
              <FontAwesomeIcon icon={faSpinner} spin className={styles.loadingSpinner} />
              <p>Loading room details...</p>
            </div>
          </main>
          {!isMobile && (
            <RightMenu
              selectedEvent={selectedEvent}
              events={events}
              onEventSelect={handleEventSelect}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.mainLayout}>
        {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
        <main className={styles.content}>
          <div className={styles.header}>
            <button 
              className={styles.backButton} 
              onClick={handleBack}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Schedule
            </button>
            <h1 className={styles.title}>{getTitle()}</h1>
          </div>

          {hotel && (
            <div className={styles.hotelInfo}>
              <h2 className={styles.hotelName}>{hotel.venueDetails?.venueData?.displayName || 'Hotel'}</h2>
              <p className={styles.hotelAddress}>{hotel.venueDetails?.venueData?.formattedAddress}</p>
              
              <div className={styles.checkInOutInfo}>
                <div className={styles.timeBlock}>
                  <span className={styles.timeLabel}>Check-In</span>
                  <span className={styles.timeDate}>{moment(hotel.checkInDateTime).format('DD MMM')}</span>
                  <span className={styles.timeHour}>{moment(hotel.checkInDateTime).format('h:mm A')}</span>
                </div>
                
                <div className={styles.timeBlock}>
                  <span className={styles.timeLabel}>Check-Out</span>
                  <span className={styles.timeDate}>{moment(hotel.checkOutDateTime).format('DD MMM')}</span>
                  <span className={styles.timeHour}>{moment(hotel.checkOutDateTime).format('h:mm A')}</span>
                </div>
                
                <div className={styles.timeBlock}>
                  <span className={styles.timeLabel}>Rooms</span>
                  <span className={styles.roomCount}>{rooms.length}</span>
                </div>
              </div>
            </div>
          )}

          <div className={styles.roomsContainer}>
            <div className={styles.roomsHeader}>
              <h3 className={styles.roomsTitle}>Rooms</h3>
              <button 
                className={styles.addButton} 
                onClick={handleAddRoom}
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Add Room</span>
              </button>
            </div>

            {rooms.length > 0 ? (
              <div className={styles.roomsList}>
                {rooms.map((room) => (
                  <div key={room.id} className={styles.roomCard}>
                    <div className={styles.roomCardHeader}>
                      <h4 className={styles.roomName}>{room.name}</h4>
                      <div className={styles.roomActions}>
                        <button 
                          className={`${styles.actionButton} ${styles.editButton}`}
                          onClick={() => handleEditRoom(room)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button 
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          onClick={() => handleDeleteRoom(room.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                    
                    <div className={styles.roomDetails}>
                      <div className={styles.detailItem}>
                        <FontAwesomeIcon icon={faIdCard} className={styles.detailIcon} />
                        <div className={styles.detailText}>
                          <span className={styles.detailLabel}>Confirmation</span>
                          <span className={styles.detailValue}>{room.confirmationNumber || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <FontAwesomeIcon icon={faDoorOpen} className={styles.detailIcon} />
                        <div className={styles.detailText}>
                          <span className={styles.detailLabel}>Room Number</span>
                          <span className={styles.detailValue}>{room.roomNumber || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <FontAwesomeIcon icon={faTag} className={styles.detailIcon} />
                        <div className={styles.detailText}>
                          <span className={styles.detailLabel}>Bag Tag</span>
                          <span className={styles.detailValue}>{room.bagTag || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <FontAwesomeIcon icon={faCalendarAlt} className={styles.detailIcon} />
                        <div className={styles.detailText}>
                          <span className={styles.detailLabel}>Check-in</span>
                          <span className={styles.detailValue}>
                            {moment(room.checkInDate).format('MMMM D, YYYY')}
                          </span>
                        </div>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <FontAwesomeIcon icon={faCalendarAlt} className={styles.detailIcon} />
                        <div className={styles.detailText}>
                          <span className={styles.detailLabel}>Check-out</span>
                          <span className={styles.detailValue}>
                            {moment(room.checkOutDate).format('MMMM D, YYYY')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faHotel} className={styles.emptyIcon} />
                <p className={styles.emptyText}>No rooms added yet</p>
                <button 
                  className={styles.addRoomButton} 
                  onClick={handleAddRoom}
                >
                  Add Room
                </button>
              </div>
            )}
          </div>

          {isModalVisible && (
            <RoomManagementModal
              isOpen={isModalVisible}
              onClose={() => {
                setIsModalVisible(false);
                setEditingRoom(null);
              }}
              onSave={handleSaveRoom}
              initialRoom={editingRoom}
              hotelCheckInDate={hotel?.checkInDateTime}
              hotelCheckOutDate={hotel?.checkOutDateTime}
            />
          )}

          {isProcessing && (
            <div className={styles.overlay}>
              <div className={styles.overlayContent}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <p>{processingText}</p>
              </div>
            </div>
          )}
        </main>
        {!isMobile && (
          <RightMenu
            selectedEvent={selectedEvent}
            events={events}
            onEventSelect={handleEventSelect}
          />
        )}
      </div>
    </div>
  );
};

export default RoomDetails;